import React, { useEffect, useMemo, useState } from "react";
import { useTableSort } from "@/lib/tableSort";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Grid3x3, Search, Download, FileText, Check, ShieldCheck } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";

export default function CommitteeMatrix() {
  const cmT = useTableSort([]);
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(null);
  const canEdit = me?.role === "super_admin";

  useEffect(() => {
    api.get("/users/committee-matrix")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Yetki matrisi yüklenemedi"));
  }, []);

  const users = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    if (!term) return data.users;
    return data.users.filter(
      (u) => (u.name || "").toLocaleLowerCase("tr").includes(term) ||
             (u.email || "").toLocaleLowerCase("tr").includes(term) ||
             (u.role_label || "").toLocaleLowerCase("tr").includes(term)
    );
  }, [data, q]);

  const committees = data?.committees || [];

  const download = async (format) => {
    setBusy(true);
    try {
      const order = cmT.applySort(users).map((u) => u.name).join(",");
      const res = await api.get("/users/committee-matrix/export", { params: { format, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yetki_matrisi.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const authorized = (u, cno) => u.all_committees || u.committee_nos.includes(cno);
  const editableRow = (u) => canEdit && u.role === "committee_manager" && !u.all_committees;

  const toggle = async (u, cno) => {
    if (!editableRow(u) || saving) return;
    const has = u.committee_nos.includes(cno);
    const next = has
      ? u.committee_nos.filter((x) => x !== cno)
      : [...u.committee_nos, cno].sort((a, b) => a - b);
    if (next.length === 0) {
      toast.error("Komite yöneticisinde en az bir komite kalmalı");
      return;
    }
    setSaving(`${u.id}-${cno}`);
    try {
      await api.patch(`/users/${u.id}`, { committee_nos: next });
      setData((d) => ({ ...d, users: d.users.map((x) => (x.id === u.id ? { ...x, committee_nos: next } : x)) }));
      toast.success(`${u.name}: K${cno} ${has ? "kaldırıldı" : "eklendi"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Güncellenemedi");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="committee-matrix-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}>
            <Grid3x3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Yetki Matrisi</h1>
            <p className="text-sm text-muted-foreground">Kullanıcıların yetkili olduğu komiteler</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid="matrix-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="matrix-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Kullanıcı ara..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="matrix-search"
        />
      </div>

      {!data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm border-collapse" data-testid="matrix-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#0F2C59] text-white">
                  <cmT.Th sortKey="cm-kullanici" get={(r) => r.name} text className="px-4 py-3 text-left font-medium sticky left-0 bg-[#0F2C59] min-w-[220px]">Kullanıcı</cmT.Th>
                  {committees.map((c) => (
                    <th key={c.committee_no} className="px-2 py-3 font-medium text-center min-w-[40px]" title={`#${c.committee_no} - ${c.name}`}>
                      {c.committee_no}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cmT.applySort(users).map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/40" data-testid={`matrix-row-${u.id}`}>
                    <td className="px-4 py-2.5 sticky left-0 bg-card">
                      <div className="font-medium text-foreground flex items-center gap-1.5">
                        {u.name}
                        {u.all_committees && <ShieldCheck className="h-3.5 w-3.5" style={{ color: GREEN }} />}
                      </div>
                      <div className="text-xs text-muted-foreground">{u.role_label}</div>
                    </td>
                    {committees.map((c) => {
                      const on = authorized(u, c.committee_no);
                      const ed = editableRow(u);
                      return (
                        <td
                          key={c.committee_no}
                          onClick={() => ed && toggle(u, c.committee_no)}
                          className={`px-2 py-2.5 text-center ${ed ? "cursor-pointer hover:bg-cyan-500/10" : ""}`}
                          data-testid={`matrix-cell-${u.id}-${c.committee_no}`}
                          title={ed ? (on ? "Kaldırmak için tıkla" : "Eklemek için tıkla") : undefined}
                        >
                          {on ? (
                            <Check className="h-4 w-4 mx-auto" style={{ color: GREEN }} />
                          ) : (
                            <span className={ed ? "text-muted-foreground/40 text-lg leading-none" : "text-muted-foreground/30"}>{ed ? "+" : "·"}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={committees.length + 1} className="px-4 py-10 text-center text-muted-foreground">
                      Eşleşen kullanıcı bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" style={{ color: GREEN }} /> işaretli kullanıcılar tüm komitelere yetkilidir.
        {canEdit && " Komite yöneticisi satırlarındaki hücrelere tıklayarak yetki ekleyip çıkarabilirsiniz."}
      </p>
    </div>
  );
}
